/**
 * Справочник слоёв чертежа: какие бывают, как называются, что в них лежит.
 *
 * В списке только слои, у которых есть своё состояние видимости (свой стор).
 * Комнат здесь нет: они вычисляются по стенам, своего стора у них нет, и
 * переключатель для них был бы пустышкой. Комментариев тоже нет: привязанный
 * комментарий виден вместе со слоем своего объекта, свободный — всегда.
 */

import type {
	Cable,
	Fixture,
	FloorItem,
	ItemsLayer,
	Pipe,
	PlanLayers,
	UnderlayLayer,
	WallGraph,
} from "./types";
import { getPlanLayer, LayerKind } from "./types";

export interface LayerInfo {
	kind: LayerKind;
	label: string;
	description: string;
	derived: boolean;
}

export const LAYERS: LayerInfo[] = [
	{
		kind: LayerKind.Wiring,
		label: "Проводка",
		description: "Группы и кабели между щитком, коробками и приборами — по потолку",
		derived: false,
	},
	{
		kind: LayerKind.Electrical,
		label: "Электрика",
		description: "Розетки, выключатели и распредкоробки на стенах, светильники на потолке",
		derived: false,
	},
	{
		kind: LayerKind.Pipes,
		label: "Водоснабжение",
		description: "Трубы ХВС и ГВС от стояков к водорозеткам — по полу",
		derived: false,
	},
	{
		kind: LayerKind.Plumbing,
		label: "Сантехника",
		description: "Ванна, унитаз, умывальник, стояки и водорозетки на стенах",
		derived: false,
	},
	{
		kind: LayerKind.Furniture,
		label: "Мебель",
		description: "Предметы, стоящие на полу",
		derived: false,
	},
	{
		kind: LayerKind.Walls,
		label: "Стены",
		description: "Стены, углы, двери и окна",
		derived: false,
	},
	{
		kind: LayerKind.WallFinish,
		label: "Отделка стен",
		description: "Краска, обои и штукатурка — по сторонам стен; видна в 3D и на развёртках",
		derived: false,
	},
	{
		kind: LayerKind.Floors,
		label: "Полы",
		description: "Напольные покрытия комнат: плитка и кварцвинил",
		derived: false,
	},
	{
		kind: LayerKind.Waterproof,
		label: "Гидроизоляция",
		description: "Гидроизоляция помещений: обмазочная, рулонная и наплавляемая",
		derived: false,
	},
	{
		kind: LayerKind.Screed,
		label: "Стяжка",
		description: "Стяжка полов: сухая, полусухая и пескобетонная",
		derived: false,
	},
	{
		kind: LayerKind.Ceiling,
		label: "Потолки",
		description: "Натяжные и гипсокартонные потолки комнат; в 3D закрывают квартиру сверху",
		derived: false,
	},
	{
		kind: LayerKind.Baseboard,
		label: "Плинтусы",
		description: "Плинтусы по внутреннему контуру комнат; разрываются у дверных проёмов",
		derived: false,
	},
	{
		kind: LayerKind.Underlay,
		label: "Подложка",
		description: "Фото или скан плана квартиры под чертежом — по нему обводят стены",
		derived: false,
	},
];

export const getLayerInfo = (kind: LayerKind): LayerInfo =>
	LAYERS.find((layer) => layer.kind === kind) ?? LAYERS[0];

/** Слои, чей объект назначается комнате целиком, а не ставится точкой: пол, гидроизоляция, стяжка, потолок. */
export const ROOM_LAYERS: LayerKind[] = [
	LayerKind.Floors,
	LayerKind.Waterproof,
	LayerKind.Screed,
	LayerKind.Ceiling,
	LayerKind.Baseboard,
];

export const ITEM_LAYERS: LayerKind[] = [
	LayerKind.Furniture,
	LayerKind.Plumbing,
	LayerKind.Electrical,
];

export const emptyWallGraph = (): WallGraph => ({ nodes: {}, walls: {}, openings: {} });

/** Подложки нет: картинки нет, масштаб 1 мм на пиксель, полупрозрачная. */
export const EMPTY_UNDERLAY: UnderlayLayer = {
	kind: LayerKind.Underlay,
	visible: true,
	image: null,
	x: 0,
	y: 0,
	mmPerPixel: 1,
	opacity: 0.5,
};

const emptyItemsLayer = <K extends LayerKind>(kind: K): ItemsLayer<K> => ({
	kind,
	visible: true,
	fixtures: {},
	items: {},
});

/**
 * Пустой проект со всеми слоями.
 */
export const getEmptyPlanLayers = (): PlanLayers => ({
	floors: { kind: LayerKind.Floors, visible: true, items: {} },
	waterproof: { kind: LayerKind.Waterproof, visible: true, items: {} },
	screed: { kind: LayerKind.Screed, visible: true, items: {} },
	rooms: { kind: LayerKind.Rooms, visible: true, items: {} },
	walls: { kind: LayerKind.Walls, visible: true, ...emptyWallGraph() },
	ceiling: { kind: LayerKind.Ceiling, visible: false, items: {} },
	baseboard: { kind: LayerKind.Baseboard, visible: true, items: {} },
	electrical: emptyItemsLayer(LayerKind.Electrical),
	plumbing: emptyItemsLayer(LayerKind.Plumbing),
	furniture: emptyItemsLayer(LayerKind.Furniture),
	wiring: { kind: LayerKind.Wiring, visible: true, items: {}, connections: {}, groups: {} },
	pipes: { kind: LayerKind.Pipes, visible: true, items: {} },
	finishes: { kind: LayerKind.WallFinish, visible: true, items: {} },
	comments: { kind: LayerKind.Comments, visible: true, items: {} },
	underlay: { ...EMPTY_UNDERLAY },
});

/**
 * Дополняет слои из старого файла недостающими: слой, которого в модели ещё не было,
 * когда проект сохраняли, получает пустое значение по умолчанию. Это единственная
 * «миграция», которую мы себе позволяем: новые слои добавляются, старые не меняются.
 */
export const withDefaultLayers = (layers: Partial<PlanLayers>): PlanLayers => {
	const empty = getEmptyPlanLayers();
	return {
		...empty,
		...layers,
		// Слой потолков был и раньше, но без данных — дополняем его пустым списком
		ceiling: { ...empty.ceiling, ...layers.ceiling },
		baseboard: { ...empty.baseboard, ...layers.baseboard },
		// Названия комнат добавили позже — старые проекты получают пустой список
		rooms: { ...empty.rooms, ...layers.rooms },
		// Связи светильников добавили позже кабелей — старые проекты получают пустой список
		wiring: { ...empty.wiring, ...layers.wiring },
	};
};

export const countLayerItems = (layers: PlanLayers, kind: LayerKind): number => {
	switch (kind) {
		case LayerKind.Walls:
			return Object.keys(layers.walls.walls).length;
		case LayerKind.Floors:
			return Object.keys(layers.floors.items).length;
		case LayerKind.Waterproof:
			return Object.keys(layers.waterproof.items).length;
		case LayerKind.Screed:
			return Object.keys(layers.screed.items).length;
		case LayerKind.Ceiling:
			return Object.keys(layers.ceiling.items).length;
		case LayerKind.Baseboard:
			return Object.keys(layers.baseboard.items).length;
		case LayerKind.Wiring:
			return Object.keys(layers.wiring.items).length;
		case LayerKind.Pipes:
			return Object.keys(layers.pipes.items).length;
		case LayerKind.WallFinish:
			return Object.keys(layers.finishes.items).length;
		case LayerKind.Comments:
			return Object.keys(layers.comments.items).length;
		case LayerKind.Underlay:
			return layers.underlay.image ? 1 : 0;
		case LayerKind.Electrical:
			return (
				Object.keys(layers.electrical.fixtures).length + Object.keys(layers.electrical.items).length
			);
		case LayerKind.Plumbing:
			return (
				Object.keys(layers.plumbing.fixtures).length + Object.keys(layers.plumbing.items).length
			);
		case LayerKind.Furniture:
			return (
				Object.keys(layers.furniture.fixtures).length + Object.keys(layers.furniture.items).length
			);
		default:
			return 0;
	}
};

export const allFixtures = (layers: PlanLayers): Fixture[] => {
	return ITEM_LAYERS.flatMap((kind) => {
		const layer = getPlanLayer(layers, kind);
		if (!("fixtures" in layer)) return [];
		return Object.values(layer.fixtures);
	});
};

export const visibleFixtures = (layers: PlanLayers): Fixture[] =>
	ITEM_LAYERS.filter((kind) => getPlanLayer(layers, kind).visible).flatMap((kind) => {
		const layer = getPlanLayer(layers, kind);
		if (!("fixtures" in layer)) return [];
		return Object.values(layer.fixtures);
	});

/** Слои, чьи `items` стоят на полу. У электрики `items` — светильники на потолке, их отдаёт visibleCeilingItems. */
const FLOOR_ITEM_LAYERS: LayerKind[] = [LayerKind.Furniture, LayerKind.Plumbing];

/** Все предметы на полу — мебель и напольная сантехника, без оглядки на видимость слоёв. */
export const allFloorItems = (layers: PlanLayers): FloorItem[] =>
	FLOOR_ITEM_LAYERS.flatMap((kind) => {
		const layer = getPlanLayer(layers, kind);
		if (!("items" in layer)) return [];
		return Object.values(layer.items);
	});

export const visibleFloorItems = (layers: PlanLayers): FloorItem[] =>
	FLOOR_ITEM_LAYERS.filter((kind) => getPlanLayer(layers, kind).visible).flatMap((kind) => {
		const layer = getPlanLayer(layers, kind);
		if (!("items" in layer)) return [];
		return Object.values(layer.items);
	});

/** Светильники на потолке — `items` слоя электрики, пока слой включён. */
export const visibleCeilingItems = (layers: PlanLayers): FloorItem[] =>
	layers.electrical.visible ? Object.values(layers.electrical.items) : [];

export const findFixture = (
	layers: PlanLayers,
	id: string,
): { layer: LayerKind; fixture: Fixture } | null => {
	for (const layer of ITEM_LAYERS) {
		const planLayer = getPlanLayer(layers, layer);
		if (!("fixtures" in planLayer)) continue;
		const fixture = planLayer.fixtures[id];
		if (fixture) return { layer, fixture };
	}
	return null;
};

export const findFloorItem = (
	layers: PlanLayers,
	id: string,
): { layer: LayerKind; item: FloorItem } | null => {
	for (const layer of ITEM_LAYERS) {
		const planLayer = getPlanLayer(layers, layer);
		//TODO разобраться с методом после
		if (!("items" in planLayer)) continue;

		const item = planLayer.items[id];
		// oxlint-disable-next-line typescript/no-explicit-any
		if (item) return { layer, item } as any;
	}
	return null;
};

export const mapItemLayers = (
	layers: PlanLayers,
	update: (layer: ItemsLayer) => ItemsLayer,
): PlanLayers => {
	let result = { ...layers };
	for (const kind of ITEM_LAYERS) {
		const layer = getPlanLayer(result, kind) as ItemsLayer;
		const updated = update(layer);

		// Обновляем результат в зависимости от kind
		switch (kind) {
			case LayerKind.Electrical:
				result = { ...result, electrical: updated as ItemsLayer<LayerKind.Electrical> };
				break;
			case LayerKind.Plumbing:
				result = { ...result, plumbing: updated as ItemsLayer<LayerKind.Plumbing> };
				break;
			case LayerKind.Furniture:
				result = { ...result, furniture: updated as ItemsLayer<LayerKind.Furniture> };
				break;
		}
	}
	return result;
};

export const mapFixtures = (
	layers: PlanLayers,
	update: (fixture: Fixture) => Fixture | null,
): PlanLayers =>
	mapItemLayers(layers, (layer) => {
		const fixtures: Record<string, Fixture> = {};
		let changed = false;
		for (const fixture of Object.values(layer.fixtures)) {
			const next = update(fixture);
			if (next !== fixture) changed = true;
			if (next) fixtures[next.id] = next;
		}
		return changed ? { ...layer, fixtures } : layer;
	});

export const cablesOfFixture = (layers: PlanLayers, fixtureId: string): Cable[] =>
	Object.values(layers.wiring.items).filter(
		(cable) => cable.from === fixtureId || cable.to === fixtureId,
	);

/** Id приборов, к которым подходит хоть один кабель, — остальные на чертеже «не подключены». */
export const wiredEndIds = (cables: Record<string, Cable>): Set<string> => {
	const wired = new Set<string>();
	for (const cable of Object.values(cables)) {
		wired.add(cable.from);
		wired.add(cable.to);
	}
	return wired;
};

/** Есть ли в слое электрики прибор с таким id — на стене или на потолке. Кабель ведут только между такими. */
export const hasCableEnd = (electrical: ItemsLayer, id: string): boolean =>
	id in electrical.fixtures || id in electrical.items;

export const dropDanglingCables = (layers: PlanLayers): PlanLayers => {
	const cables: Record<string, Cable> = {};
	let changed = false;
	for (const cable of Object.values(layers.wiring.items)) {
		if (hasCableEnd(layers.electrical, cable.from) && hasCableEnd(layers.electrical, cable.to)) {
			cables[cable.id] = cable;
		} else {
			changed = true;
		}
	}
	return changed ? { ...layers, wiring: { ...layers.wiring, items: cables } } : layers;
};

// ---- трубы водоснабжения -----------------------------------------------------

export const pipesOfFixture = (layers: PlanLayers, fixtureId: string): Pipe[] =>
	Object.values(layers.pipes.items).filter(
		(pipe) => pipe.from === fixtureId || pipe.to === fixtureId,
	);

/** Id точек, к которым подходит хоть одна труба, — остальные на чертеже «не подключены». */
export const pipedEndIds = (pipes: Record<string, Pipe>): Set<string> => {
	const piped = new Set<string>();
	for (const pipe of Object.values(pipes)) {
		piped.add(pipe.from);
		piped.add(pipe.to);
	}
	return piped;
};

/**
 * Есть ли в слое сантехники объект на стене с таким id. Точка ли это водоснабжения,
 * а не умывальник, здесь не проверяется — это знает каталог (`isWaterPoint`).
 */
export const hasPipeEnd = (plumbing: ItemsLayer, id: string): boolean => id in plumbing.fixtures;

export const dropDanglingPipes = (layers: PlanLayers): PlanLayers => {
	const pipes: Record<string, Pipe> = {};
	let changed = false;
	for (const pipe of Object.values(layers.pipes.items)) {
		if (hasPipeEnd(layers.plumbing, pipe.from) && hasPipeEnd(layers.plumbing, pipe.to)) {
			pipes[pipe.id] = pipe;
		} else {
			changed = true;
		}
	}
	return changed ? { ...layers, pipes: { ...layers.pipes, items: pipes } } : layers;
};
